import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'

const AUTH_FEATURES = [
  {
    title: 'Expected Value Finder',
    desc: 'Positive EV opportunities with real-time odds across 100+ bookmakers',
    accent: '#10b981',
    icon: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>
  },
  {
    title: 'Arbitrage Finder',
    desc: 'Risk-free profit by covering both sides of a market',
    accent: '#a78bfa',
    icon: <><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></>
  },
  {
    title: 'Middles Finder',
    desc: 'Middle opportunities with qualifying loss and effective odds built in',
    accent: '#38bdf8',
    icon: <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>
  },
  {
    title: 'Bonus Converter',
    desc: 'Turn bonus bets into withdrawable cash at 70%+ retention',
    accent: '#f59e0b',
    icon: <><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></>
  },
  {
    title: 'Bet Tracker',
    desc: 'ROI, profit charts and more across every bet you place',
    accent: '#ec4899',
    icon: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>
  },
  {
    title: 'Education & Resources',
    desc: 'Learn every strategy step-by-step with our modules',
    accent: '#22d3ee',
    icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>
  }
]

const FIELD_ICONS = {
  mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>
}

// Labelled, icon-prefixed input with a focus ring — mirrors the app's Field
// component in bookiecharity-mobile-app (auth)/login.tsx.
const Field = ({ icon, type, value, onChange, placeholder, autoComplete, right }) => {
  const [focused, setFocused] = React.useState(false)
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#0f1626',
      border: `1px solid ${focused ? '#3b82f6' : '#1e2a40'}`,
      borderRadius: '12px',
      padding: '0 14px',
      boxShadow: focused ? '0 0 0 3px rgba(59, 130, 246, 0.12)' : 'none',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
    }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={focused ? '#60a5fa' : '#475569'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: 'stroke 0.15s ease' }}>
        {FIELD_ICONS[icon]}
      </svg>
      <input
        type={type}
        className="bc-auth-input"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: '#f1f5f9',
          fontSize: '14.5px',
          padding: '13px 12px',
          minWidth: 0
        }}
      />
      {right}
    </div>
  )
}

const EyeButton = ({ shown, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={shown ? 'Hide password' : 'Show password'}
    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: '#64748b', flexShrink: 0 }}
  >
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {shown ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </>
      )}
    </svg>
  </button>
)

const s = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(4, 8, 16, 0.78)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px 0',
    overflowY: 'auto'
  },
  modalLeft: {
    flex: '1',
    minWidth: '320px',
    maxWidth: '400px'
  },
  modalHeader: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    marginBottom: '24px' 
  },
  title: {
    fontSize: '26px',
    fontWeight: '800',
    letterSpacing: '-0.02em',
    margin: 0,
    color: '#ffffff'
  },
  closeBtn: { 
    backgroundColor: 'transparent', 
    border: 'none', 
    color: '#94a3b8', 
    fontSize: '24px', 
    cursor: 'pointer', 
    padding: '4px' 
  },
  form: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '16px' 
  },
  input: {
    border: '1px solid #26354f',
    borderRadius: '9px',
    padding: '12px 16px',
    backgroundColor: '#101b30',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
  },
  button: {
    padding: '14px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '15px',
    transition: 'background-color 0.15s ease',
    boxShadow: '0 2px 10px rgba(37, 99, 235, 0.25)'
  },
  buttonSecondary: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    borderRadius: '6px',
    border: '1px solid #26354f',
    cursor: 'pointer',
    fontSize: '14px'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#2a1216',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#fca5a5',
    fontSize: '13px'
  },
  success: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#0e241c',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#34d399',
    fontSize: '13px'
  },
  switchMode: { 
    textAlign: 'center', 
    marginTop: '16px' 
  },
  link: { 
    color: '#3b82f6', 
    cursor: 'pointer', 
    textDecoration: 'underline' 
  },
  oauthButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#101b30',
    color: '#f1f5f9',
    borderRadius: '9px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    width: '100%',
    transition: 'filter 0.15s ease'
  },
  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#ffffff',
    color: '#1f2937',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    width: '100%'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '16px 0',
    color: '#5b6678',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#1b2438',
    margin: '0 12px'
  }
}

export const AuthModal = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const [mode, setMode] = useState(initialMode) // signin, signup, reset, newpassword
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  // Email address awaiting confirmation. Set after a sign-up that returned no
  // session, which is what Supabase does when email confirmation is on.
  const [pendingEmail, setPendingEmail] = useState('')
  const [resendState, setResendState] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  const { signIn, signUp, resetPassword, updatePassword, isPasswordResetRequired } = useAuth()

  // Handle window resize for responsive layout
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Re-apply the caller's requested mode each time the modal opens (e.g. the
  // mobile welcome screen opens it in 'signup' via Get Started).
  React.useEffect(() => {
    if (isOpen && !isPasswordResetRequired) setMode(initialMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode])

  // Listen for password recovery event from auth context
  React.useEffect(() => {
    const handlePasswordRecovery = () => {
      setMode('newpassword')
    }

    window.addEventListener('password-recovery', handlePasswordRecovery)
    return () => window.removeEventListener('password-recovery', handlePasswordRecovery)
  }, [])

  // If password reset is required, force newpassword mode
  React.useEffect(() => {
    if (isPasswordResetRequired && isOpen) {
      setMode('newpassword')
      setError('')
      setMessage('')
    }
  }, [isPasswordResetRequired, isOpen])

  if (!isOpen) return null

  const handleOAuthSignIn = async (provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: process.env.NODE_ENV === 'production' 
            ? 'https://app.bookiecharity.com' 
            : window.location.origin
        }
      })
      if (error) throw error
    } catch (error) {
      setError(error.message)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password)
        if (error) throw error
        onClose()
      } else if (mode === 'signup') {
        const { data, error } = await signUp(email, password)
        if (error) throw error
        // Supabase creates a live session on sign-up (no email confirmation),
        // so a successful sign-up is a login. Close the modal and let the app
        // render once auth state updates — same as the sign-in branch. If no
        // session came back (confirmation on), fall back to the sign-in prompt.
        if (data?.session) {
          onClose()
        } else {
          // No session means email confirmation is on: the account exists but
          // cannot be used until the link is clicked. Hand off to the inbox
          // screen rather than the sign-in form, which would just fail.
          setPendingEmail(email)
        }
      } else if (mode === 'reset') {
        const { error } = await resetPassword(email)
        if (error) throw error
        setMessage('Password reset email sent!')
        setLoading(false)
      } else if (mode === 'newpassword') {
        const { error } = await updatePassword(newPassword)
        if (error) throw error
        setMessage('Password updated successfully!')
        setTimeout(() => {
          onClose()
          setMode('signin')
        }, 2000)
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleResendConfirmation = async () => {
    setResendState('sending')
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail
      })
      // A 60s per-user throttle sits in front of this (Supabase SMTP setting),
      // so a rapid second press is an expected outcome, not a fault.
      setResendState(error ? 'error' : 'sent')
    } catch (e) {
      setResendState('error')
    }
  }

  const resetForm = () => {
    setEmail('')
    setPassword('')
    setNewPassword('')
    setError('')
    setMessage('')
    setPendingEmail('')
    setResendState('')
  }

  const switchMode = (newMode) => {
    setMode(newMode)
    resetForm()
  }

  const modalStyle = {
    backgroundColor: '#010714',
    borderRadius: '16px',
    padding: isMobile ? '20px' : '32px',
    border: '1px solid #1e2c47',
    maxWidth: isMobile ? '480px' : ((!pendingEmail && (mode === 'signin' || mode === 'signup')) ? '1000px' : '460px'),
    width: '90%',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '28px' : '40px',
    position: 'relative',
    maxHeight: 'calc(100vh - 40px)',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    margin: '0 auto',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: isMobile ? 'none' : 'auto',
    msOverflowStyle: isMobile ? 'none' : 'auto'
  }

  const modalRightStyle = {
    flex: '1',
    minWidth: isMobile ? '0' : '400px',
    borderLeft: isMobile ? 'none' : '1px solid #1b2438',
    paddingLeft: isMobile ? '0' : '48px',
    borderTop: isMobile ? '1px solid #1b2438' : 'none',
    paddingTop: isMobile ? '32px' : '0'
  }

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <style>{`
          .bc-auth-input::placeholder { color: #475569; }
          /* Chrome/Safari autofill paints inputs white — repaint with an inset
             shadow (background-color itself can't be overridden) */
          .bc-auth-input:-webkit-autofill,
          .bc-auth-input:-webkit-autofill:hover,
          .bc-auth-input:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 1000px #0f1626 inset;
            -webkit-text-fill-color: #f1f5f9;
            caret-color: #f1f5f9;
            border-radius: 0;
            transition: background-color 600000s 0s;
          }
        `}</style>
        {/* Left Side - Login Form */}
        <div style={pendingEmail ? { ...s.modalLeft, maxWidth: 'none', width: '100%' } : s.modalLeft}>
          {pendingEmail ? (
          /* Sign-up succeeded but the account is unconfirmed. This is a dead end
             by design: there is nothing useful the user can do in this tab until
             they open the link, so the form is replaced rather than sitting
             behind a message inviting them to sign in and fail. */
          <div style={{ textAlign: 'center', padding: isMobile ? '8px 0' : '16px 0' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(96,165,250,0.1)',
              border: '1px solid rgba(96,165,250,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </div>

            <h2 style={{ ...s.title, marginBottom: '10px' }}>Confirm your email</h2>

            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', margin: '0 0 6px 0' }}>
              We sent a confirmation link to
            </p>
            <p style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600', margin: '0 0 20px 0', wordBreak: 'break-all' }}>
              {pendingEmail}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              Click the link in that email to activate your account. You can close this window.
            </p>

            <div style={{
              background: 'rgba(15,23,42,0.6)',
              border: '1px solid #1b2438',
              borderRadius: '10px',
              padding: '14px 16px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#64748b', fontSize: '12.5px', lineHeight: '1.6', margin: 0 }}>
                Nothing yet? Check your spam folder, or resend it below.
              </p>
            </div>

            {resendState === 'sent' ? (
              <p style={{ color: '#34d399', fontSize: '13px', margin: '0 0 16px 0' }}>
                Confirmation email resent.
              </p>
            ) : resendState === 'error' ? (
              <p style={{ color: '#f87171', fontSize: '13px', margin: '0 0 16px 0' }}>
                Could not resend just yet. Wait a minute and try again.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resendState === 'sending'}
                style={{
                  ...s.button,
                  background: 'transparent',
                  border: '1px solid #2c3d5c',
                  color: '#e2e8f0',
                  marginBottom: '16px'
                }}
              >
                {resendState === 'sending' ? 'Sending...' : 'Resend confirmation email'}
              </button>
            )}

            <div style={{ textAlign: 'center' }}>
              <span
                onClick={() => { setPendingEmail(''); setResendState(''); switchMode('signin') }}
                style={{ color: '#60a5fa', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Back to sign in
              </span>
            </div>
          </div>
          ) : (
          <>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '16px' }}>
            <img
              src="https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/BC_LOGO_200x200.png"
              alt="Bookie Charity Logo"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                marginBottom: isMobile ? '16px' : '12px'
              }}
            />
          </div>

          <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '20px' }}>
            <h2 style={s.title}>
              {mode === 'signin' && 'Welcome back'}
              {mode === 'signup' && 'Create your account'}
              {mode === 'reset' && 'Reset password'}
              {mode === 'newpassword' && 'Set new password'}
            </h2>
            {mode === 'signin' && (
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: '8px 0 0 0' }}>
                Sign in to your account
              </p>
            )}
            {mode === 'signup' && (
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: '8px 0 0 0' }}>
                Start your profitable betting journey
              </p>
            )}
          </div>

          {error && (
            <div style={{ ...s.error, marginBottom: '14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ flex: 1 }}>{error}</span>
            </div>
          )}
          {message && (
            <div style={{ ...s.success, marginBottom: '14px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span style={{ flex: 1 }}>{message}</span>
            </div>
          )}

          {/* OAuth Buttons */}
        {(mode === 'signin' || mode === 'signup') && (
          <div style={{ marginBottom: '16px' }}>
            <button
              type="button"
              onClick={() => handleOAuthSignIn('google')}
              style={s.googleButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => handleOAuthSignIn('discord')}
                style={{ ...s.oauthButton, backgroundColor: '#5865F2' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.120.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Continue with Discord
              </button>
            </div>

            <div style={s.divider}>
              <div style={s.dividerLine}></div>
              <span>or</span>
              <div style={s.dividerLine}></div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={s.form}>
          {mode !== 'newpassword' && (
            <Field
              icon="mail"
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}

          {(mode === 'signin' || mode === 'signup') && (
            <Field
              icon="lock"
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              right={<EyeButton shown={showPw} onToggle={() => setShowPw(v => !v)} />}
            />
          )}

          {mode === 'newpassword' && (
            <Field
              icon="lock"
              type={showPw ? 'text' : 'password'}
              placeholder="Enter new password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              right={<EyeButton shown={showPw} onToggle={() => setShowPw(v => !v)} />}
            />
          )}

          {mode === 'signin' && (
            <div style={{ textAlign: 'right', marginTop: '-6px' }}>
              <span
                onClick={() => switchMode('reset')}
                style={{ color: '#60a5fa', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Forgot password?
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={s.button}
          >
            {loading ? 'Loading...' : (
              mode === 'signin' ? 'Sign in' :
              mode === 'signup' ? 'Create account' :
              mode === 'reset' ? 'Send reset email' :
              'Update password'
            )}
          </button>
        </form>

        {mode === 'signup' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '12px', lineHeight: '1.4' }}>
              By signing up, you agree to our{' '}
              <a
                href="https://bookiecharity.com/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                style={{ ...s.link, fontSize: '12px' }}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="https://bookiecharity.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
                style={{ ...s.link, fontSize: '12px' }}
              >
                Privacy Policy
              </a>
            </p>
          </div>
        )}

        <div style={s.switchMode}>
          {mode === 'signin' && (
            <>
              <div style={{
                backgroundColor: 'rgba(16, 185, 129, 0.07)',
                border: '1px solid rgba(52, 211, 153, 0.25)',
                borderRadius: '11px',
                padding: '16px',
                margin: '16px 0',
                textAlign: 'center'
              }}>
                <p style={{ color: '#f1f5f9', fontSize: '14px', margin: '0 0 4px 0', fontWeight: '600' }}>
                  New to Bookie Charity?
                </p>
                <p style={{ color: '#8b97ab', fontSize: '12.5px', margin: '0 0 12px 0' }}>
                  Free to join — no card required
                </p>
                <button
                  onClick={() => switchMode('signup')}
                  style={{
                    ...s.button,
                    backgroundColor: '#16a34a',
                    boxShadow: '0 2px 10px rgba(22, 163, 74, 0.25)',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#15803d'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#16a34a'
                  }}
                >
                  Create Free Account
                </button>
              </div>
            </>
          )}
          
          {mode === 'signup' && (
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>
              Already have an account?{' '}
              <span onClick={() => switchMode('signin')} style={s.link}>
                Sign in
              </span>
            </p>
          )}
          
          {mode === 'reset' && (
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>
              Remember your password?{' '}
              <span onClick={() => switchMode('signin')} style={s.link}>
                Sign in
              </span>
            </p>
          )}
        </div>
          </>
          )}
        </div>

        {/* Right Side - Features Section */}
        {!pendingEmail && (mode === 'signin' || mode === 'signup') && (
          <div style={modalRightStyle}>
            <h2 style={{ fontSize: isMobile ? '22px' : '23px', fontWeight: '700', color: '#f1f5f9', marginBottom: isMobile ? '8px' : '6px', lineHeight: '1.3', wordWrap: 'break-word' }}>
              Beat The Bookies At Their Own Game
            </h2>
            <p style={{ color: '#94a3b8', fontSize: isMobile ? '14px' : '13px', lineHeight: '1.5', marginBottom: isMobile ? '28px' : '20px', wordWrap: 'break-word' }}>
              Bookie Charity provides everything you need to start making regular income off the bookmakers with a mathematical approach to betting
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '13px' }}>
              {AUTH_FEATURES.map((f) => (
                <div key={f.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    borderRadius: '9px',
                    background: `${f.accent}1f`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={f.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {f.icon}
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#f1f5f9', margin: '0 0 2px 0' }}>
                      {f.title}
                    </h3>
                    <p style={{ fontSize: '12.5px', color: '#8b97ab', margin: 0, lineHeight: '1.45' }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              ))}
              <p style={{ fontSize: '12px', color: '#5b6678', margin: '2px 0 0 44px', lineHeight: '1.5' }}>
                Plus calculators, live odds comparison, the full bookmaker list and more.
              </p>
            </div>

            {/* Link to marketing site */}
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #1b2438' }}>
              <a
                href="https://bookiecharity.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#94a3b8',
                  backgroundColor: 'transparent',
                  border: '1px solid #26354f',
                  borderRadius: '7px',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(148, 163, 184, 0.06)'
                  e.target.style.borderColor = '#35486b'
                  e.target.style.color = '#f1f5f9'
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent'
                  e.target.style.borderColor = '#26354f'
                  e.target.style.color = '#94a3b8'
                }}
              >
                Learn More
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}